import { useState, useEffect, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { unidadesApi } from '../../api/client'
import { formatFechaHora } from './tipos'
import MentionTextarea from '../menciones/MentionTextarea'
import TextoConMenciones from '../menciones/TextoConMenciones'

const MAX_BYTES = 10 * 1024 * 1024
const MAX_ARCHIVOS = 10
const esImagen = (a) => (a.mime_type || a.type || '').startsWith('image/')

function iniciales(nombre) {
  return (nombre || '?').split(/\s+/).filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 2)
}

// A photo attached to a comment. The file needs the auth header, so it is
// fetched into a blob: URL (revoked when the thumbnail goes away).
function Miniatura({ archivo, api }) {
  const [url, setUrl] = useState(null)
  useEffect(() => {
    let vivo = true, u = null
    api.comentarioArchivoUrl(archivo).then(x => { u = x; if (vivo) setUrl(x); else URL.revokeObjectURL(x) }).catch(() => {})
    return () => { vivo = false; if (u) URL.revokeObjectURL(u) }
  }, [archivo, api])
  return (
    <a href={url || undefined} target="_blank" rel="noopener noreferrer" title={archivo.filename}
      className="block w-24 h-24 rounded-lg overflow-hidden bg-gray-100 border border-gray-200 hover:opacity-90">
      {url && <img src={url} alt={archivo.filename} className="w-full h-full object-cover" />}
    </a>
  )
}

// A unit's conversation: everyone with access reads and writes; each message
// shows who wrote it and when (both set by the server), with its files.
export default function Conversacion({ unidadId, api = unidadesApi, onCantidad }) {
  const { t } = useTranslation('engineering')
  const [comentarios, setComentarios] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [texto, setTexto] = useState('')
  const [adjuntos, setAdjuntos] = useState([])     // File objects waiting to be sent
  const [enviando, setEnviando] = useState(false)
  const fileInput = useRef(null)
  const fondo = useRef(null)

  useEffect(() => {
    let vivo = true
    setLoading(true)
    api.getComentarios(unidadId)
      .then(cs => { if (vivo) { setComentarios(cs); setError('') } })
      .catch(() => { if (vivo) setError(t('unidades.conversacion.loadFailed')) })
      .finally(() => { if (vivo) setLoading(false) })
    return () => { vivo = false }
  }, [unidadId, t, api])

  // Tell whoever shows a counter (the bubble on the partes board) how many messages there are.
  useEffect(() => { if (!loading) onCantidad?.(comentarios.length) }, [comentarios, loading, onCantidad])

  // Newest message in view after loading and after sending.
  useEffect(() => { fondo.current?.scrollIntoView({ block: 'end' }) }, [comentarios])

  // Previews for the files about to be sent, freed when the list changes.
  const previews = useMemo(() => adjuntos.map(f => (esImagen(f) ? URL.createObjectURL(f) : null)), [adjuntos])
  useEffect(() => () => previews.forEach(u => u && URL.revokeObjectURL(u)), [previews])

  const agregar = (files) => {
    setError('')
    const nuevos = [...files]
    const grandes = nuevos.filter(f => f.size > MAX_BYTES)
    if (grandes.length) setError(t('unidades.planilla.fileTooBig', { name: grandes.map(f => f.name).join(', ') }))
    setAdjuntos(as => {
      const todos = [...as, ...nuevos.filter(f => f.size > 0 && f.size <= MAX_BYTES)]
      if (todos.length > MAX_ARCHIVOS) setError(t('unidades.conversacion.tooManyFiles', { n: MAX_ARCHIVOS }))
      return todos.slice(0, MAX_ARCHIVOS)
    })
    if (fileInput.current) fileInput.current.value = ''
  }

  const puedeEnviar = (texto.trim() || adjuntos.length) && !enviando

  const enviar = async () => {
    if (!puedeEnviar) return
    setEnviando(true)
    setError('')
    try {
      const c = await api.postComentario(unidadId, texto.trim(), adjuntos)
      setComentarios(cs => [...cs, c])
      setTexto('')
      setAdjuntos([])
    } catch (err) {
      const detail = err.response?.data?.detail
      setError(`${t('unidades.conversacion.sendFailed')}${typeof detail === 'string' ? ` (${detail})` : ''}`)
    } finally {
      setEnviando(false)
    }
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-1">
        {loading ? (
          <p className="text-gray-400 text-sm">{t('unidades.loading')}</p>
        ) : comentarios.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-10">{t('unidades.conversacion.empty')}</p>
        ) : comentarios.map(c => (
          <div key={c.id} className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-gray-900 text-white text-xs font-semibold flex items-center justify-center shrink-0">
              {iniciales(c.usuario_nombre)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-sm font-semibold text-gray-900">{c.usuario_nombre}</span>
                <span className="text-xs text-gray-400 tabular-nums">{formatFechaHora(c.creado_en)}</span>
              </div>
              {c.texto && <p className="text-sm text-gray-700 whitespace-pre-wrap break-words mt-0.5"><TextoConMenciones texto={c.texto} /></p>}
              {c.archivos.length > 0 && (
                <div className="mt-2 space-y-2">
                  {c.archivos.some(esImagen) && (
                    <div className="flex flex-wrap gap-2">
                      {c.archivos.filter(esImagen).map(a => <Miniatura key={a.id} archivo={a} api={api} />)}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-1.5">
                    {c.archivos.filter(a => !esImagen(a)).map(a => (
                      <button key={a.id} type="button"
                        onClick={() => api.downloadComentarioArchivo(a).catch(() => setError(t('unidades.planilla.openFailed')))}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-100 text-xs text-gray-700 hover:bg-gray-200">
                        📎 {a.filename}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
        <div ref={fondo} />
      </div>

      <div className="pt-3 mt-3 border-t border-gray-100 space-y-2">
        {error && <p className="text-red-500 text-xs">{error}</p>}
        {adjuntos.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {adjuntos.map((f, i) => (
              <div key={`${f.name}-${i}`} className="relative">
                {previews[i]
                  ? <img src={previews[i]} alt={f.name} className="w-14 h-14 rounded-md object-cover border border-gray-200" />
                  : <div className="h-14 px-2 flex items-center rounded-md bg-gray-100 text-xs text-gray-600 max-w-[10rem] truncate">📎 {f.name}</div>}
                <button type="button" onClick={() => setAdjuntos(as => as.filter((_, j) => j !== i))}
                  title={t('unidades.conversacion.removeFile')}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-gray-900 text-white text-xs leading-none">×</button>
              </div>
            ))}
          </div>
        )}
        <MentionTextarea rows={3} value={texto} onChange={setTexto}
          placeholder={t('unidades.conversacion.placeholder')}
          onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); enviar() } }}
          onPaste={e => { const fs = [...e.clipboardData.files]; if (fs.length) { e.preventDefault(); agregar(fs) } }}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white resize-none focus:outline-none focus:ring-2 focus:ring-gray-200" />
        <div className="flex items-center justify-between gap-2">
          <input ref={fileInput} type="file" multiple className="hidden" onChange={e => agregar(e.target.files)} />
          <button type="button" onClick={() => fileInput.current?.click()} disabled={enviando}
            className="text-xs font-medium text-gray-600 hover:text-gray-900">
            {t('unidades.conversacion.attach')}
          </button>
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-gray-400 hidden sm:inline">{t('unidades.conversacion.shortcut')}</span>
            <button type="button" onClick={enviar} disabled={!puedeEnviar}
              className="bg-gray-900 text-white px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-40 transition-colors">
              {enviando ? t('unidades.conversacion.sending') : t('unidades.conversacion.send')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
